import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { PackageOpen, Plus } from 'lucide-react';
import { useState } from 'react';
import { formatMoney } from '@nokowazzi/types';
import type { Paginated } from '@nokowazzi/types';
import { Badge, Button, EmptyState, Input, Select, Spinner } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { Can } from '../features/admin/Can';
import type { AdminProductRow } from '../features/admin/queries';
import { ProductStatusBadge } from '../features/products/status';
import { apiFetch } from '../lib/api';
import { imagePublicUrl } from '../lib/storage';
import { jsonLocaleText, jsonStringArray } from '../lib/json-i18n';

export const Route = createFileRoute('/_admin/products/')({
  component: ProductsScreen,
});

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: '전체 상태' },
  { value: 'draft', label: '초안' },
  { value: 'published', label: '게시중' },
  { value: 'archived', label: '보관됨' },
];

function ProductsScreen() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');

  const listQuery = useInfiniteQuery({
    queryKey: ['admin', 'products', 'list', { q, status: statusFilter }] as const,
    queryFn: ({ pageParam }) =>
      apiFetch<Paginated<AdminProductRow>>('/v1/admin/products', {
        query: {
          q: q || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          cursor: pageParam ?? undefined,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const items = listQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title="상품 관리"
        actions={
          <Can perm="products:write">
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="size-4" aria-hidden="true" />}
              onClick={() => void navigate({ to: '/products/new' })}
            >
              상품 등록
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex items-center gap-2">
        <Select
          className="w-40"
          aria-label="상태 필터"
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onValueChange={setStatusFilter}
        />
        <form
          className="w-72"
          onSubmit={(event) => {
            event.preventDefault();
            setQ(searchInput.trim());
          }}
        >
          <Input
            type="search"
            placeholder="상품명 검색 (Enter)"
            aria-label="상품명 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            clearable
            clearLabel="검색어 지우기"
          />
        </form>
      </div>

      {listQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="상품 목록 불러오는 중" />
        </div>
      ) : listQuery.isError ? (
        <EmptyState
          title="상품 목록을 불러오지 못했습니다"
          description="잠시 후 다시 시도해 주세요"
          action={
            <Button variant="tertiary" size="sm" onClick={() => void listQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<PackageOpen size={40} strokeWidth={1.5} />}
          title="상품이 없습니다"
          description="우측 상단의 상품 등록 버튼으로 첫 상품을 만들어 보세요"
        />
      ) : (
        <>
          <Table>
            <Thead>
              <tr>
                <Th>상품명</Th>
                <Th className="w-24">상태</Th>
                <Th className="w-32 text-right">가격</Th>
                <Th className="w-20 text-center">추천</Th>
                <Th className="w-24 text-right">노출순서</Th>
                <Th className="w-20 text-right">
                  <span className="sr-only">액션</span>
                </Th>
              </tr>
            </Thead>
            <tbody>
              {items.map((product) => {
                const thumbnail = jsonStringArray(product.images)[0];
                return (
                  <Tr key={product.id}>
                    <Td>
                      <div className="flex items-center gap-3">
                        {thumbnail != null ? (
                          <img
                            src={imagePublicUrl(thumbnail)}
                            alt=""
                            className="size-10 shrink-0 rounded-md border border-line object-cover"
                          />
                        ) : (
                          <div className="size-10 shrink-0 rounded-md border border-line bg-subtle" />
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium text-fg">
                            {jsonLocaleText(product.title_i18n, 'ko')}
                          </p>
                          <p className="truncate text-caption2 text-fg-tertiary">
                            {jsonLocaleText(product.title_i18n, 'ja')}
                          </p>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <ProductStatusBadge status={product.status} />
                    </Td>
                    <Td className="text-right tabular-nums">
                      {formatMoney({ amount: product.price_amount, currency: product.currency })}
                    </Td>
                    <Td className="text-center">
                      {product.is_featured ? (
                        <Badge tone="neutral">추천</Badge>
                      ) : (
                        <span className="text-fg-tertiary">—</span>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums">{product.sort_order}</Td>
                    <Td className="text-right">
                      <Link
                        to="/products/$id"
                        params={{ id: product.id }}
                        className="inline-flex h-8 items-center rounded-md bg-subtle px-2.5 font-medium text-fg transition-colors hover:bg-neutral-200"
                      >
                        수정
                      </Link>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          {listQuery.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="tertiary"
                size="sm"
                loading={listQuery.isFetchingNextPage}
                onClick={() => void listQuery.fetchNextPage()}
              >
                더 보기
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
